/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/require-default-props */
import PropTypes from 'prop-types';
import React, { createRef, useCallback, useEffect, useRef, useState } from 'react';
import { Col, Row, OverlayTrigger } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { nanoid } from 'nanoid';
import {
  getSurveyQuestionBySurveyIdSectionAndQuestionIdApi,
  getSurveyQuestionsCategoriesApi,
} from '../../../../api/surveys';
import Chart from '../../../../Components/Chart';
import types from '../../../../types';
import { ONBOARDING_STEPS } from '../../../../constants/common';
import benTooltip from '../../../../Components/BenTooltip';
import useOnboarding from '../../hooks/useOnboarding';
import Question404Dialog from './question404Dialog';

const DETAIL_FETCH_ATTEMPTS = 3;
const DETAIL_RETRY_DELAY_MS = 1500;

// One question preview in the section list. Owns its expanded question detail
// and categories as hook state. The expansion state itself stays in redux
// (SURVEY_DETAILS_ONLY_OPEN_QUESTIONS / openedQuestions) because the shared
// Chart component reads it from the store.
const SectionQuestion = ({
  question,
  sectionId,
  index,
  visualizations,
  refreshToken,
  onSelectedQuestion,
  onLoadQuestion,
  onQuestionRefreshed,
}) => {
  const dispatch = useDispatch();
  const storeJwt = useSelector((state) => state.session.jwt);
  const { openedQuestions } = useSelector((state) => state.surveyDetails);
  const { isStepVisible, completeStep } = useOnboarding();
  const [currentQuestion, setCurrentQuestion] = useState(question);
  const [questionDetail, setQuestionDetail] = useState(null);
  const [categories, setCategories] = useState([]);
  const lastRefreshTokenRef = useRef(refreshToken);

  // The query service can still be recomputing right after the survey cache is
  // cleared, answering with no result set. Retry a few times so the chart ends
  // up on freshly computed data instead of silently keeping the old preview.
  const fetchQuestionDetail = useCallback(
    async (questionId) => {
      for (let attempt = 0; attempt < DETAIL_FETCH_ATTEMPTS; attempt += 1) {
        // eslint-disable-next-line no-await-in-loop
        const detailData = await getSurveyQuestionBySurveyIdSectionAndQuestionIdApi(
          visualizations.contextId,
          sectionId,
          questionId,
          visualizations,
          storeJwt
        );
        if (detailData?.queryResult?.mliDataSegments?.length) {
          return detailData;
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, DETAIL_RETRY_DELAY_MS));
      }
      return null;
    },
    [sectionId, storeJwt, visualizations]
  );

  const handleQuestionClick = useCallback(
    async (questionId) => {
      if (!visualizations || visualizations.length <= 0) return;
      dispatch({
        type: types.reducerTypes.SURVEY_DETAILS_ONLY_OPEN_QUESTIONS,
        payload: { questionId },
      });
      if (!questionId) return;
      onLoadQuestion(questionId);
      try {
        const [detailData, categoryData] = await Promise.all([
          fetchQuestionDetail(questionId),
          getSurveyQuestionsCategoriesApi(questionId, storeJwt),
        ]);
        if (detailData) setQuestionDetail(detailData);
        setCategories((prev) => [...prev, categoryData]);
      } catch (e) {
        console.error(e);
        Question404Dialog(dispatch);
      }
      onSelectedQuestion(questionId);
    },
    [dispatch, fetchQuestionDetail, onLoadQuestion, onSelectedQuestion, storeJwt, visualizations]
  );

  const updateChartType = (currJob, chartName) => {
    const updatedVisu = { ...visualizations };
    const defaultSettings = {
      benchmarks: {
        state: false,
        national: false,
      },
      showPrincipalResponses: false,
    };
    updatedVisu.details.chartSettings.chartSettings[currJob.id] = {
      ...(updatedVisu?.details?.chartSettings?.chartSettings[currJob.id] || defaultSettings),
      selectedChartType: chartName,
    };
  };

  const updateBenchmark = (currJob, benchmarks) => {
    const updatedVisu = { ...visualizations };
    updatedVisu.details.chartSettings.chartSettings[currJob.id] = {
      benchmarks,
      showPrincipalResponses: false,
      selectedChartType:
        updatedVisu?.details?.chartSettings?.chartSettings[currJob.id]?.selectedChartType ||
        currJob?.defaultVisualizationType,
    };
  };

  useEffect(() => {
    if (questionDetail && openedQuestions.includes(question.id)) {
      setCurrentQuestion(questionDetail);
    } else {
      setCurrentQuestion(question);
      setCategories([]);
    }
  }, [questionDetail, question, openedQuestions]);

  // The page bumps refreshToken after a question save has landed: reload the
  // expanded question so its preview reflects the saved changes, then tell the
  // page the refresh is done so it can drop the loading overlay.
  useEffect(() => {
    if (refreshToken === lastRefreshTokenRef.current) return;
    lastRefreshTokenRef.current = refreshToken;
    if (openedQuestions.includes(question.id)) {
      (async () => {
        try {
          await handleQuestionClick(question.id);
        } finally {
          if (onQuestionRefreshed) onQuestionRefreshed();
        }
      })();
    }
  }, [refreshToken, openedQuestions, question.id, handleQuestionClick, onQuestionRefreshed]);

  const saveStepHandler = () => {
    completeStep(ONBOARDING_STEPS.step2.value);
  };

  const showStep2 = isStepVisible(ONBOARDING_STEPS.step2) && index === 0;

  return (
    <Row>
      <Col>
        {currentQuestion && (
          <OverlayTrigger
            key={question?.id}
            placement="bottom"
            defaultShow={showStep2}
            show={showStep2}
            overlay={(props) =>
              benTooltip({
                ...props,
                title: ONBOARDING_STEPS.step2.title,
                content: ONBOARDING_STEPS.step2.content,
                close: saveStepHandler,
              })
            }
          >
            <Chart
              key={nanoid()}
              currentJob={currentQuestion}
              visualization={visualizations || {}}
              currentCategory={categories?.find((qc) => qc?.key_id === currentQuestion?.id)}
              questionsCategories={categories}
              isRelated={!!currentQuestion.relatedQuestionId}
              spaceAdded={false}
              isEditing
              updateChartType={updateChartType}
              updateBenchmark={updateBenchmark}
              onExport={() => {}}
              ref={createRef()}
              onSelectedQuestion={handleQuestionClick}
            />
          </OverlayTrigger>
        )}
      </Col>
    </Row>
  );
};

SectionQuestion.propTypes = {
  question: PropTypes.instanceOf(Object).isRequired,
  sectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  index: PropTypes.number.isRequired,
  visualizations: PropTypes.instanceOf(Object),
  refreshToken: PropTypes.number,
  onSelectedQuestion: PropTypes.func.isRequired,
  onLoadQuestion: PropTypes.func.isRequired,
  onQuestionRefreshed: PropTypes.func,
};

export default SectionQuestion;
